import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Gauge, RefreshCw, TrendingUp, TrendingDown, Truck, MapPin, Loader2, Download } from "lucide-react";

const WINDOW_OPTIONS = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
];

function fmtMiles(n) {
  if (n == null) return "—";
  return `${Math.round(n).toLocaleString()} mi`;
}

function fmtPct(p) {
  if (p == null) return "—";
  return `${Math.round(p)}%`;
}

function effColor(pct) {
  if (pct == null) return "bg-slate-100 text-slate-600";
  if (pct >= 70) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (pct >= 50) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-red-100 text-red-700 border-red-200";
}

/**
 * Read-only Admin Dispatch panel — fleet efficiency & deadhead miles.
 *
 * Backend: GET /api/admin/efficiency?since_hours=N&top_n=10
 * Auto-refresh every 90s. No mutations. Mobile-first layout.
 */
export default function DispatchEfficiencyPanel({ auth, API }) {
  const [windowHours, setWindowHours] = useState(168);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEfficiency = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/admin/efficiency?since_hours=${windowHours}&top_n=10`,
        auth()
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, auth, windowHours]);

  const downloadCsv = useCallback(async () => {
    try {
      const a = auth();
      const basic = a?.auth ? `${a.auth.username}:${a.auth.password}` : null;
      const res = await axios.get(
        `${API}/admin/efficiency/csv?since_hours=${windowHours}&top_n=200`,
        {
          ...(basic ? { headers: { Authorization: `Basic ${btoa(basic)}` } } : {}),
          responseType: "blob",
        }
      );
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const label = windowHours >= 168 ? `${windowHours / 24}d` : `${windowHours}h`;
      link.download = `efficiency_${label}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "CSV export failed");
    }
  }, [API, auth, windowHours]);

  useEffect(() => {
    fetchEfficiency();
    const t = setInterval(fetchEfficiency, 90000);
    return () => clearInterval(t);
  }, [fetchEfficiency]);

  const efficiencyPct = data?.fleet_efficiency_pct;
  const totalMiles = data?.total_miles_driven || 0;
  const paidMiles = data?.total_paid_miles || 0;
  const deadhead = data?.total_deadhead_miles || 0;
  const deadheadPct = totalMiles > 0 ? (deadhead / totalMiles) * 100 : null;
  const drivers = data?.top_drivers || [];

  // Top + bottom utilisation for at-a-glance ranking.
  const sortedByEff = [...drivers].filter(d => d.efficiency_pct != null)
    .sort((a, b) => b.efficiency_pct - a.efficiency_pct);
  const top = sortedByEff.slice(0, 3);
  const bottom = sortedByEff.length > 3 ? sortedByEff.slice(-3).reverse() : [];

  return (
    <Card data-testid="efficiency-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Gauge className="h-4 w-4 text-cyan-600" />
            Fleet efficiency &amp; deadhead
          </CardTitle>
          <div className="flex items-center gap-1">
            {WINDOW_OPTIONS.map(opt => (
              <button
                key={opt.hours}
                onClick={() => setWindowHours(opt.hours)}
                className={`text-[11px] px-2 py-1 rounded-md transition ${
                  windowHours === opt.hours
                    ? "bg-cyan-100 text-cyan-700 font-semibold"
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`efficiency-window-${opt.label}`}
              >
                {opt.label}
              </button>
            ))}
            <button
              onClick={fetchEfficiency}
              className="p-1 text-slate-400 hover:text-slate-700 ml-1"
              title="Refresh"
              data-testid="efficiency-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={downloadCsv}
              className="ml-1 text-[11px] px-2 py-1 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 flex items-center gap-1"
              title="Export efficiency window as CSV"
              data-testid="efficiency-csv-btn"
            >
              <Download className="w-3 h-3" /> CSV
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Derived from GPS breadcrumbs. Read-only — no dispatch settings affected.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="efficiency-error">
            {error}
          </div>
        )}

        {!data && loading && (
          <div className="py-6 text-center text-slate-400" data-testid="efficiency-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        )}

        {data && (
          <>
            {/* Headline KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-3" data-testid="efficiency-kpi-fleet">
                <p className="text-[10px] uppercase tracking-wide text-cyan-700">Fleet eff.</p>
                <p className="text-2xl font-bold text-cyan-900">{fmtPct(efficiencyPct)}</p>
                <p className="text-[10px] text-cyan-700 mt-0.5">paid ÷ total miles</p>
              </div>
              <div className="bg-violet-50 border border-violet-100 rounded-lg p-3" data-testid="efficiency-kpi-deadhead">
                <p className="text-[10px] uppercase tracking-wide text-violet-700">Deadhead</p>
                <p className="text-2xl font-bold text-violet-900">{fmtPct(deadheadPct)}</p>
                <p className="text-[10px] text-violet-700 mt-0.5">{fmtMiles(deadhead)} empty</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid="efficiency-kpi-paid">
                <p className="text-[10px] uppercase tracking-wide text-amber-700">Paid miles</p>
                <p className="text-2xl font-bold text-amber-900">{fmtMiles(paidMiles)}</p>
                <p className="text-[10px] text-amber-700 mt-0.5">of {fmtMiles(totalMiles)} total</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3" data-testid="efficiency-kpi-jobs">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700">Jobs done</p>
                <p className="text-2xl font-bold text-emerald-900">{data.completed_jobs_count}</p>
                <p className="text-[10px] text-emerald-700 mt-0.5">{data.accepted_jobs_count} accepted</p>
              </div>
            </div>

            {/* Paid vs deadhead stacked bar */}
            {totalMiles > 0 && (
              <div data-testid="efficiency-bar">
                <div className="flex h-3 rounded-full overflow-hidden border border-slate-200">
                  <div
                    className="bg-cyan-500"
                    style={{ width: `${(paidMiles / totalMiles) * 100}%` }}
                    title={`Paid ${fmtMiles(paidMiles)}`}
                  />
                  <div
                    className="bg-violet-400"
                    style={{ width: `${(deadhead / totalMiles) * 100}%` }}
                    title={`Deadhead ${fmtMiles(deadhead)}`}
                  />
                </div>
                <div className="flex justify-between text-[11px] text-slate-500 mt-1">
                  <span><span className="inline-block w-2 h-2 rounded-full bg-cyan-500 mr-1" />Paid</span>
                  <span><span className="inline-block w-2 h-2 rounded-full bg-violet-400 mr-1" />Deadhead</span>
                </div>
              </div>
            )}

            {/* Top / bottom utilisation */}
            {drivers.length === 0 && (
              <div className="text-center py-6 text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg" data-testid="efficiency-empty">
                <Truck className="w-5 h-5 mx-auto text-slate-300 mb-1" />
                No GPS breadcrumb activity in this window yet.
              </div>
            )}

            {drivers.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div data-testid="efficiency-top-drivers">
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-emerald-700 mb-1.5 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Top utilisation
                  </p>
                  <ul className="space-y-1">
                    {top.length === 0 && (
                      <li className="text-[11px] text-slate-400">Awaiting more data…</li>
                    )}
                    {top.map(d => (
                      <li key={d.driver_id} className="flex items-center justify-between text-xs py-1.5 px-2 bg-emerald-50 border border-emerald-100 rounded-md" data-testid={`efficiency-top-${d.driver_id}`}>
                        <span className="font-semibold text-slate-800 truncate">{d.name}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-slate-500">{fmtMiles(d.miles)}</span>
                          <Badge className={`text-[10px] ${effColor(d.efficiency_pct)}`}>{fmtPct(d.efficiency_pct)}</Badge>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                {bottom.length > 0 && (
                  <div data-testid="efficiency-bottom-drivers">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-amber-700 mb-1.5 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" /> Lowest utilisation
                    </p>
                    <ul className="space-y-1">
                      {bottom.map(d => (
                        <li key={d.driver_id} className="flex items-center justify-between text-xs py-1.5 px-2 bg-amber-50 border border-amber-100 rounded-md" data-testid={`efficiency-bottom-${d.driver_id}`}>
                          <span className="font-semibold text-slate-800 truncate">{d.name}</span>
                          <span className="flex items-center gap-2">
                            <span className="text-slate-500">{fmtMiles(d.miles)}</span>
                            <Badge className={`text-[10px] ${effColor(d.efficiency_pct)}`}>{fmtPct(d.efficiency_pct)}</Badge>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Detailed driver table */}
            {drivers.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-x-auto" data-testid="efficiency-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2">Driver</th>
                      <th className="text-right px-3 py-2">Total</th>
                      <th className="text-right px-3 py-2">Paid</th>
                      <th className="text-right px-3 py-2">Deadhead</th>
                      <th className="text-right px-3 py-2">Eff.</th>
                      <th className="text-right px-3 py-2">Jobs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {drivers.map(d => (
                      <tr key={d.driver_id} className="hover:bg-slate-50" data-testid={`efficiency-row-${d.driver_id}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{d.name}</td>
                        <td className="text-right px-3 py-2 text-slate-700 font-mono">{fmtMiles(d.miles)}</td>
                        <td className="text-right px-3 py-2 text-cyan-700 font-mono">{fmtMiles(d.paid_miles)}</td>
                        <td className="text-right px-3 py-2 text-violet-700 font-mono">{fmtMiles(d.deadhead_miles)}</td>
                        <td className="text-right px-3 py-2">
                          <Badge className={`text-[10px] ${effColor(d.efficiency_pct)}`}>{fmtPct(d.efficiency_pct)}</Badge>
                        </td>
                        <td className="text-right px-3 py-2 text-slate-700 font-mono">{d.jobs_completed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-[10px] text-slate-400 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Auto-refresh every 90s · Window: {windowHours >= 168 ? `${windowHours / 24}d` : `${windowHours}h`}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
